/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Monday, January 10th 2022, 10:40:28 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useWatch } from "react-hook-form";
import CodeIcon from "@mui/icons-material/Code";
import { useOSFormContext } from "ui-widgets/forms/providers";
import ApiIcon from "@mui/icons-material/Api";
import StyleIcon from "@mui/icons-material/Style";
import { UseAPIFormSpec } from "lib/api";
import { AutoSaveAPIForm, SwitchFormInput } from "ui-widgets/forms";
import { TaskTemplate, USER_TASK_TYPES } from "lib/schemas";
import { useTranslatedText } from "core";
import Stack from "@mui/material/Stack";
import { Archive, Trash2 } from "react-feather";
import Box from "@mui/material/Box";
import { ContentTaggingPopup } from "common/components/content-tags";
import { useTaskTemplateAPIForm } from "service-builder/services";
import { WorkflowTaskIntegrations } from "service-builder/components/integrations/WorkflowTaskIntegrations";
import { TabbedNavigator } from "ui-widgets/tabs/TabbedNavigator";
import { ObjectValue } from "lib/types";
import { TranslatableTextFormInput } from "translation/components";
import { HelperText, PopupMenu, useToggleState } from "ui-widgets";
import { ReviewTaskSettings } from "./ReviewTaskSettings";
import { TaskAssignmentSettings, TaskAssignmentFormInput } from "./TaskAssignmentSettings";
import { PaymentTaskSettings } from "./PaymentTaskSettings";
import { BillingTaskSettings } from "./BillingTaskSettings";
import { DocumentTaskSettings } from "./DocumentTaskSettings";
import { DatasetUpdateTaskSettings } from "./DatasetUpdateTaskSettings";
import { CalledElementSettings } from "./DownstreamProcessesSettings";
import { BoundaryEventsSettings, EventNodeTypeSettings } from "./EventOchestrationSettings";
import { SigningTaskSettings } from "./SigningTaskSettings";
import { UserTaskCommonSettings } from "./UserTaskCommonSettings";
import { TaskUndoSettings } from "./TaskUndoSettings";
import { TaskAccessSettings } from "./TaskAccessSettings";
import { TaskNotificationSettings } from "./TaskNotificationSettings";
import { TaskStageSettings } from "./TaskStageSettings";
import { UserTaskTypeFormInput } from "./TaskTypeFormInputs";
// import {  AutomatedTaskTypeFormInput } from "./TaskTypeFormInputs";
import { SplitNodeSettings } from "./SplitNodeSettings";
import { DataAssociationNodeSetting } from "./DataAssociationNodeSetting";
import { NextNodeSetting } from "./NextNodeSetting";
import { AITaskSettings } from "./AITaskSettings";
import { ResubmissionTaskSettings } from "./ResubmissionTaskSettings";
import { TaskTemplateCustomSettings } from "./TaskTemplateCustomSettings";

interface TaskTemplateFormProps {
  taskTemplateData: TaskTemplate;
  taskTemplateSaveCallbackFn: UseAPIFormSpec<TaskTemplate>["setAPIFormResponse"];
  handleDeleteTaskTemplate: () => void;
  handleDisableTaskTemplate: () => void;
}

export const TaskTemplateForm = (props: TaskTemplateFormProps) => {
  const useTaskTemplateAPIFormReturn = useTaskTemplateAPIForm(
    props.taskTemplateData?.id,
    props.taskTemplateData,
    props.taskTemplateSaveCallbackFn
  );

  const {
    useFormReturn: { watch },
  } = useTaskTemplateAPIFormReturn;

  const [
    taskTemplateType,
    taskTemplateTitle,
    billingConfigurationId,
    generateDocumentFlag,
    documentTemplateId,
    isClientTask,
    datasetReferenceId,
    aiAgentConfigurationId,
  ] = watch<(keyof TaskTemplate)[]>([
    "type",
    "title",
    "billing_configuration",
    "generate_document",
    "document",
    "client_task",
    "dataset_reference",
    "ai_task_instruction",
  ]) as [
    TaskTemplate["type"],
    TaskTemplate["title"],
    TaskTemplate["billing_configuration"],
    TaskTemplate["generate_document"],
    TaskTemplate["document"],
    TaskTemplate["client_task"],
    TaskTemplate["dataset_reference"],
    TaskTemplate["ai_task_instruction"]
  ];

  const userActionType = watch<keyof TaskTemplate>("user_action_type") as keyof typeof USER_TASK_TYPES;
  const LANG = useTranslatedText();
  const isStartTask = taskTemplateType === "StartFunction";
  const isHandlerTask = taskTemplateType === "Handler";
  const isEndTask = taskTemplateType === "End";
  const isUserTask = taskTemplateType === "View";
  const isSplitTask = taskTemplateType === "Split";
  const isMergeTask = taskTemplateType === "Join";
  const isJobTask = taskTemplateType === "Job";
  const isCallTask = taskTemplateType === "Call";
  const isEventTask = taskTemplateType === "Event";

  return (
    <AutoSaveAPIForm showFeedbackAlerts={false} useAPIFormReturn={useTaskTemplateAPIFormReturn} loaderType="linear">
      <Stack direction="row" spacing={1}>
        <TranslatableTextFormInput<TaskTemplate> label={LANG.title} schema_field_key="title" sx={{ ml: 3 }} />
        <div>
          <TaskTemplateMoreActions
            taskTemplate={props.taskTemplateData}
            handleDeleteTaskTemplate={props.handleDeleteTaskTemplate}
            handleDisableTaskTemplate={props.handleDisableTaskTemplate}
          />
        </div>
      </Stack>

      <div style={{ marginLeft: 24, marginRight: 20 }}>
        {isUserTask && <UserTaskTypeFormInput />}

        {/* {isAutomatedTask && <AutomatedTaskTypeFormInput />} */}
        <Stack direction="row" spacing={2}>
          <div style={{ width: "100%" }}>
            <NextNodeSetting />
          </div>
          <div style={{ width: "100%" }}>
            <DataAssociationNodeSetting />
          </div>
        </Stack>
      </div>
      {isUserTask && (
        <Stack>
          <UserTaskSettings
            taskTemplateTitle={taskTemplateTitle}
            userActionType={userActionType}
            isClientTask={isClientTask as boolean}
          />
        </Stack>
      )}

      {isSplitTask && (
        <Stack>
          <SplitNodeSettings />
        </Stack>
      )}
      {isMergeTask && (
        <Stack>
          <MergeNodeFields />
        </Stack>
      )}
      {isJobTask && (
        <Stack>
          <JobTaskFields />
        </Stack>
      )}
      {isHandlerTask && (
        <Stack>
          <ServiceTaskSettings
            billingConfigurationId={billingConfigurationId}
            generateDocumentFlag={generateDocumentFlag}
            documentTemplateId={documentTemplateId}
            datasetReferenceId={datasetReferenceId}
            aiAgentConfigurationId={aiAgentConfigurationId}
          />
        </Stack>
      )}
      {isStartTask && (
        <Stack>
          <StartNodeSettings billingConfigurationId={billingConfigurationId} />
        </Stack>
      )}
      {isEndTask && <EndNodeSettings />}
      {isEventTask && <EventNodeSettings />}
      {isCallTask && <CallNodeSettings />}
    </AutoSaveAPIForm>
  );
};

interface TaskTemplateMoreActionsProps {
  taskTemplate: TaskTemplate;
  handleDeleteTaskTemplate: () => void;
  handleDisableTaskTemplate: () => void;
}

const TaskTemplateMoreActions = ({
  taskTemplate,
  handleDeleteTaskTemplate,
  handleDisableTaskTemplate,
}: TaskTemplateMoreActionsProps) => {
  const {
    handleClose: handleCloseIntegrations,
    handleOpen: handleOpenIntegrations,
    open: integrationsOpenState,
  } = useToggleState();

  const {
    handleClose: handleCloseCustomSettings,
    handleOpen: handleOpenCustomSettings,
    open: customSettingsOpenState,
  } = useToggleState();

  const {
    handleClose: handleCloseContentTagging,
    handleOpen: handleOpenContentTagging,
    open: contentTaggingOpenState,
  } = useToggleState();

  const isStartNode = taskTemplate.type === "StartFunction";
  let menuItems = [
    { icon: <ApiIcon />, onClick: handleOpenIntegrations, text: "Integrations" },
    { icon: <CodeIcon />, onClick: handleOpenCustomSettings, text: `Custom settings for ${taskTemplate?.title}` },
    { icon: <StyleIcon />, onClick: handleOpenContentTagging, text: "Task Template Tags" },
  ];
  if (!isStartNode) {
    menuItems = [
      ...menuItems,
      { icon: <Archive size="16" />, onClick: handleDisableTaskTemplate, text: "Deactivate Task Template" },
      { icon: <Trash2 size="16" />, onClick: handleDeleteTaskTemplate, text: "Delete" },
    ];
  }

  const { watch, setValue, reset } = useOSFormContext<TaskTemplate>();

  const [watchedTagIds] = watch<(keyof TaskTemplate)[]>(["tags"]) as [TaskTemplate["tags"]];
  return (
    <>
      <PopupMenu menuItems={menuItems} />

      <WorkflowTaskIntegrations
        open={integrationsOpenState}
        onClose={handleCloseIntegrations}
        taskTemplate={taskTemplate}
      />

      <ContentTaggingPopup
        open={contentTaggingOpenState}
        onClose={handleCloseContentTagging}
        organizationId={taskTemplate.artifact_org}
        selectedTagIds={watchedTagIds}
        onSelectContentTag={(contentTag) => setValue("tags", [...watchedTagIds, contentTag.id], { shouldDirty: true })}
        onDeselectContentTag={(contentTag) => {
          // Reset the form default values to current, so that next save with shouldDirty=true always triggers auto-save even
          // if the tags value is the same as what was there when the form was initialized with useForm.
          // shouldDirty = true only triggers if the default values have changed since initializaton, so if one does multiple changes
          // and reverts back to initial state, should dirty will not trigger, thinking values have not change. We do the reset to make it
          // know values are changing.
          // https://react-hook-form.com/docs/useform/formstate
          // https://react-hook-form.com/docs/useform/reset
          // https://docs.google.com/document/d/1PWeCUNNF0dQFAjnC9_-SXkVdtqXgYtAxDwNhi7IXMaE/edit#heading=h.71sxbvic2qrh
          reset(watch());
          setValue(
            "tags",
            watchedTagIds.filter((tagId) => tagId !== contentTag.id),
            { shouldDirty: true }
          );
        }}
      />

      <TaskTemplateCustomSettings open={customSettingsOpenState} onClose={handleCloseCustomSettings} />
    </>
  );
};

interface UserTaskFieldsProps {
  taskTemplateTitle: ObjectValue<TaskTemplate>;
  userActionType: keyof typeof USER_TASK_TYPES;
  isClientTask: TaskTemplate["client_task"];
}

const UserTaskSettings = ({ userActionType, taskTemplateTitle, isClientTask }: UserTaskFieldsProps) => {
  let userActionTabs: React.ReactNode[];
  switch (userActionType) {
    case "review":
      userActionTabs = [
        <>{taskTemplateTitle ?? "Assessment Task"} Settings</>,
        <ReviewTaskSettings />,
        <>Task Assignment Settings</>,
        <TaskAssignmentSettings />,
        <>Undo Task Settings</>,
        <TaskUndoSettings />,
      ];
      break;
    case "billing":
      userActionTabs = [
        <>{taskTemplateTitle ?? "Billing Task"} Settings</>,
        <BillingTaskSettings />,
        <>Task Assignment Settings</>,
        <TaskAssignmentSettings />,
        <>Undo Task Settings</>,
        <TaskUndoSettings />,
      ];
      break;
    case "payment":
      userActionTabs = [<>{taskTemplateTitle ?? "Payment Task"} Settings</>, <PaymentTaskSettings />];
      break;
    case "payment_confirmation":
      userActionTabs = [
        <>{taskTemplateTitle ?? "Payment Confirmation Task"} Settings</>,
        <UserTaskCommonSettings />,
        <>Task Assignment Settings</>,
        <TaskAssignmentSettings />,
      ];
      break;
    case "sign":
      userActionTabs = [
        <>{taskTemplateTitle ?? "Signing Task"} Settings</>,
        <SigningTaskSettings />,
        <>Task Assignment Settings</>,
        <TaskAssignmentSettings />,
        <>Undo Task Settings</>,
        <TaskUndoSettings />,
      ];
      break;
    case "resubmission":
      userActionTabs = [
        <>{taskTemplateTitle ?? "Resubmission Task"} Settings</>,
        <>
          <ResubmissionTaskSettings />
          <UserTaskCommonSettings />
        </>,
      ];
      break;
    case "assignment":
      userActionTabs = [
        <>{taskTemplateTitle ?? "Process Assignment"} Settings</>,
        <TaskAssignmentFormInput taskTitle={taskTemplateTitle} />,
      ];
      break;
    case "move":
      userActionTabs = [
        <>{taskTemplateTitle ?? "Forward Transition"} Settings</>,
        <UserTaskCommonSettings />,
        <>Task Assignment Settings</>,
        <TaskAssignmentSettings />,
        <>Undo Task Settings</>,
        <TaskUndoSettings />,
      ];
      break;
    default:
      userActionTabs = [
        <>{taskTemplateTitle ?? "Custom Task"} Settings</>,
        <ReviewTaskSettings />,
        <>Task Assignment Settings</>,
        <TaskAssignmentSettings />,
        <>Undo Task Settings</>,
        <TaskUndoSettings />,
      ];
  }

  const { control } = useOSFormContext<TaskTemplate>();
  const watchedTaskTemplate = useWatch({ control }) as TaskTemplate;

  return (
    <Box sx={{ width: "100%" }}>
      <TabbedNavigator variant="fullWidth">
        {userActionTabs.map((userActionTab, index) => (
          <div key={`${index}`}>{userActionTab}</div>
        ))}
        <>Notifications Templates</>
        <TaskNotificationSettings />
        {!isClientTask ? <>Team Access</> : null}
        {!isClientTask ? <TaskAccessSettings taskTemplate={watchedTaskTemplate} /> : null}
        <>Stage Settings</>
        <TaskStageSettings />
        <>Events</>
        <BoundaryEventsSettings />
      </TabbedNavigator>
    </Box>
  );
};

interface StartNodeSettingsProps {
  billingConfigurationId: TaskTemplate["billing_configuration"];
}

const StartNodeSettings = ({ billingConfigurationId }: StartNodeSettingsProps) => {
  const [invoiceOnStart, setInvoiceOnStart] = React.useState(Boolean(billingConfigurationId));
  const showBillingConfigurationSelection = invoiceOnStart || Boolean(billingConfigurationId);

  React.useEffect(() => {
    setInvoiceOnStart(Boolean(billingConfigurationId));
  }, [billingConfigurationId]);

  return (
    <Box sx={{ width: "100%" }}>
      <TabbedNavigator variant="fullWidth">
        <>Upfront Billing Settings</>
        <>
          {!billingConfigurationId && (
            <SwitchFormInput
              schema_field_key=""
              label="Should users be billed before the process can proceed?"
              checked={invoiceOnStart}
              onChange={(e, checked: boolean) => setInvoiceOnStart(checked)}
            />
          )}
          {showBillingConfigurationSelection && <BillingTaskSettings />}
        </>
        <>Notification Templates</>
        <TaskNotificationSettings />
        <>Stage Settings</>
        <TaskStageSettings />
      </TabbedNavigator>
    </Box>
  );
};

const EndNodeSettings = () => {
  return (
    <Box sx={{ width: "100%" }}>
      <TabbedNavigator variant="fullWidth">
        <>Notification Templates</>
        <TaskNotificationSettings />
        <>Stage Settings</>
        <TaskStageSettings />
        <>Events</>
        <BoundaryEventsSettings />
      </TabbedNavigator>
    </Box>
  );
};

interface ServiceTaskFieldsProps {
  billingConfigurationId: ObjectValue<TaskTemplate>;
  generateDocumentFlag: ObjectValue<TaskTemplate>;
  documentTemplateId: ObjectValue<TaskTemplate>;
  datasetReferenceId: ObjectValue<TaskTemplate>;
  aiAgentConfigurationId: TaskTemplate["ai_task_instruction"];
}

/** ServiceTaskSettings can render both BillingTaskSettings and DocumentTemplateSettings because
 * OneStop supports execution of both automated billing and automated document generation in one
 * viewflow activation.
 */
const ServiceTaskSettings = ({
  billingConfigurationId,
  generateDocumentFlag,
  documentTemplateId,
  datasetReferenceId,
  aiAgentConfigurationId,
}: ServiceTaskFieldsProps) => {
  const serviceTaskSettings: React.ReactNode[] = [];

  if (billingConfigurationId) {
    serviceTaskSettings.push(<>Auto-Generated Bill Settings</>);

    serviceTaskSettings.push(<BillingTaskSettings />);
  }

  if (generateDocumentFlag && documentTemplateId) {
    serviceTaskSettings.push(<>Document Generation Settings</>);

    serviceTaskSettings.push(<DocumentTaskSettings />);
  }

  if (datasetReferenceId) {
    serviceTaskSettings.push(<>Dataset Update Settings</>);

    serviceTaskSettings.push(<DatasetUpdateTaskSettings />);
  }

  if (aiAgentConfigurationId) {
    serviceTaskSettings.push(<>AI Task Settings</>);
    serviceTaskSettings.push(<AITaskSettings aiAgentConfigurationId={aiAgentConfigurationId} />);
  }

  return (
    <Box sx={{ width: "100%" }}>
      <TabbedNavigator variant="fullWidth">
        {serviceTaskSettings.map((serviceTaskSettingTabElement, index) => (
          <div key={`${index}`}>{serviceTaskSettingTabElement}</div>
        ))}
        <>Notification Templates</>
        <TaskNotificationSettings />
        <>Stage Settings</>
        <TaskStageSettings />
        <>Events</>
        <BoundaryEventsSettings />
      </TabbedNavigator>
    </Box>
  );
};

const MergeNodeFields = () => (
  <Box sx={{ width: "100%" }}>
    <TabbedNavigator variant="fullWidth">
      <>Merge Task Settings</>
      <HelperText>
        The merge task will wait for all incoming tasks to complete before it acitvates its next node/task. Kindly just
        set the next step to activate in the input above.
      </HelperText>
    </TabbedNavigator>
  </Box>
);

const JobTaskFields = () => (
  <Box sx={{ width: "100%" }}>
    <TabbedNavigator variant="fullWidth">
      <>Scheduled Job Settings</>
      <HelperText>Configure Scheduled task logic below.</HelperText>
    </TabbedNavigator>
  </Box>
);

const EventNodeSettings = () => {
  return (
    <Box sx={{ width: "100%" }}>
      <TabbedNavigator variant="fullWidth">
        <>Event</>
        <EventNodeTypeSettings />
      </TabbedNavigator>
    </Box>
  );
};

const CallNodeSettings = () => {
  return (
    <Box sx={{ width: "100%" }}>
      <TabbedNavigator variant="fullWidth">
        <>Notification Templates</>
        <TaskNotificationSettings />
        <>Stage Settings</>
        <TaskStageSettings />
        <>Called Element</>
        <CalledElementSettings />
        <>Events</>
        <BoundaryEventsSettings />
      </TabbedNavigator>
    </Box>
  );
};
